import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/errors";

const SLOW_LOAD_MS = 15000;

export default function EmbeddedApp() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const [app, setApp] = useState(null);
  const [launchUrl, setLaunchUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loadingPhase, setLoadingPhase] = useState("connecting"); // connecting | iframe | slow | ready
  const [reloadKey, setReloadKey] = useState(0);

  const mintAndSet = useCallback(async () => {
    const { data } = await api.post(`/sso/launch/${appId}`);
    // Cache-bust the embedded app shell. The Hub trusts the remote app's
    // cache headers, so on a redeploy the iframe will happily serve an
    // older `index.html` until the browser revalidates. Appending a
    // monotonic `_cb` (cache-buster) on every mint forces the URL to
    // change, which makes the browser issue a fresh request even if the
    // app sends long-lived cache headers. This is a best-effort defence;
    // see HUB_SSO_RETROFIT_PROMPT.md (§1A) — apps should also send
    // `Cache-Control: no-store` on `index.html`.
    const sep = data.launch_url.includes("?") ? "&" : "?";
    const bustedUrl = `${data.launch_url}${sep}_cb=${Date.now()}`;
    setLaunchUrl(bustedUrl);
    return { ...data, launch_url: bustedUrl };
  }, [appId]);

  // Load app metadata + initial launch
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingPhase("connecting");
      setError(null);
      try {
        const { data: appData } = await api.get(`/apps/${appId}`);
        if (!alive) return;
        if (!appData.embeddable) {
          toast.message(`${appData.name} can't be embedded`, {
            description: "Opened in a new tab instead.",
          });
          const { data: launch } = await api.post(`/sso/launch/${appId}`);
          window.open(launch.launch_url, "_blank", "noopener");
          navigate("/apps", { replace: true });
          return;
        }
        setApp(appData);
        await mintAndSet();
        if (alive) setLoadingPhase("iframe");
      } catch (e) {
        const status = e?.response?.status;
        const detail = formatApiError(e);
        if (status === 403) {
          toast.error(detail || "You don't have access to this app");
          navigate("/apps", { replace: true });
          return;
        }
        if (status === 404) {
          toast.error("App not found");
          navigate("/apps", { replace: true });
          return;
        }
        if (alive) setError(detail || "Couldn't connect to the app.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [appId, navigate, mintAndSet, reloadKey]);

  // Slow-load watchdog
  useEffect(() => {
    if (loadingPhase !== "iframe") return undefined;
    const t = setTimeout(() => setLoadingPhase("slow"), SLOW_LOAD_MS);
    return () => clearTimeout(t);
  }, [loadingPhase, reloadKey]);

  // postMessage token-refresh bridge
  useEffect(() => {
    if (!app) return undefined;
    let expectedOrigin;
    try {
      expectedOrigin = new URL(app.url).origin;
    } catch {
      return undefined;
    }
    const handler = async (evt) => {
      if (evt.origin !== expectedOrigin) return;
      const data = evt.data;
      if (!data || typeof data !== "object") return;
      if (data.type !== "cargodash.token.refresh") return;
      try {
        const { data: minted } = await api.post(`/sso/launch/${appId}`);
        iframeRef.current?.contentWindow?.postMessage(
          { type: "cargodash.token", token: minted.token },
          expectedOrigin
        );
      } catch {
        iframeRef.current?.contentWindow?.postMessage(
          { type: "cargodash.token.error", error: "refresh_failed" },
          expectedOrigin
        );
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [app, appId]);

  const openInTab = async () => {
    try {
      const { data } = await api.post(`/sso/launch/${appId}`);
      window.open(data.launch_url, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiError(e) || "Launch failed");
    }
  };

  const refreshIframe = async () => {
    setLoadingPhase("iframe");
    setReloadKey((k) => k + 1);
  };

  return (
    <div
      data-testid="embedded-app"
      className="flex h-screen w-screen flex-col bg-white"
    >
      {/* Utility bar */}
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/apps")}
          data-testid="embedded-back"
          className="gap-1.5 text-slate-600"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Apps</span>
        </Button>
        <div className="mx-2 h-5 w-px bg-slate-200" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-sm font-semibold text-slate-900">
            {app?.name || "Connecting…"}
          </div>
          {app && (
            <div className="truncate text-[11px] text-slate-500">
              {new URL(app.url).host}
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refreshIframe}
          disabled={!launchUrl}
          data-testid="embedded-refresh"
          className="gap-1.5 text-slate-600"
          title="Reload with a fresh token"
        >
          <RotateCcw className="h-4 w-4" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={openInTab}
          disabled={!app}
          data-testid="embedded-newtab"
          className="gap-1.5"
        >
          <ExternalLink className="h-4 w-4" />
          <span className="hidden sm:inline">Open in new tab</span>
        </Button>
      </div>

      {/* Body */}
      <div className="relative flex-1 bg-slate-50">
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="max-w-md rounded-xl border border-cargodash-alert-600/20 bg-white p-6 text-center shadow-sm">
              <AlertTriangle className="mx-auto h-8 w-8 text-cargodash-alert-600" />
              <div className="mt-3 font-display text-base font-semibold text-slate-900">
                Couldn't open {app?.name || "this app"}
              </div>
              <p className="mt-1 text-sm text-slate-500">{error}</p>
              <div className="mt-4 flex justify-center gap-2">
                <Button variant="outline" onClick={() => navigate("/apps")}>
                  Back to apps
                </Button>
                <Button
                  className="bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
                  onClick={refreshIframe}
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        ) : !launchUrl ? (
          <div
            data-testid="embedded-connecting"
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="flex flex-col items-center gap-3 text-slate-500">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 animate-pulse rounded-full bg-cargodash-primary-500" />
                <span className="h-2 w-2 animate-pulse rounded-full bg-cargodash-primary-500 [animation-delay:120ms]" />
                <span className="h-2 w-2 animate-pulse rounded-full bg-cargodash-primary-500 [animation-delay:240ms]" />
              </div>
              <div className="text-sm font-medium">
                Connecting to {app?.name || "app"}…
              </div>
            </div>
          </div>
        ) : (
          <>
            <iframe
              key={reloadKey}
              ref={iframeRef}
              data-testid="embedded-iframe"
              src={launchUrl}
              title={app?.name || "App"}
              allow="clipboard-read; clipboard-write"
              referrerPolicy="no-referrer"
              sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              onLoad={() => setLoadingPhase("ready")}
              className="absolute inset-0 h-full w-full border-0 bg-white"
            />
            {loadingPhase === "slow" && (
              <div
                data-testid="embedded-slow"
                className="pointer-events-none absolute inset-x-0 top-0 flex justify-center p-4"
              >
                <div className="pointer-events-auto flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-md">
                  <AlertTriangle className="h-4 w-4 text-cargodash-action" />
                  <div className="text-sm text-slate-700">
                    Taking too long?
                  </div>
                  <Button size="sm" variant="outline" onClick={openInTab}>
                    Open in new tab
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
